import React, { useEffect, useCallback, useState } from 'react';
import { FaBrain, FaCogs, FaRobot } from 'react-icons/fa';
import TaskEngineIcon from '../agents/TaskEngineIcon';
import EvaluationMetricsService from '../../../src/services/api/EvaluationMetrics';
import AgentService from '../../services/api/agents';
import { showNotificationGlobal } from '../common/NotificationProvider';
import SAModalConfirm from '../common/SAModalConfirm';
import { toolNodes } from '../Workflow/NodePalette';

const METRIC_PILL_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#ec4899', '#14b8a6'];
const EVALUATE_DOT_COLORS = ['#6366f1', '#0ea5e9', '#a855f7', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

const AGENT_STATUS_STYLES = {
  Configured: { dot: '#22c55e', text: '#15803d' },
  Incomplete: { dot: '#f59e0b', text: '#b45309' },
  'Not Configured': { dot: '#3b82f6', text: '#1d4ed8' },
};

const KIND_COLORS = {
  'AI Agent': '#a259e6',
  'Task Agent': '#f59e0b',
};

const getAgentInitials = (name) => {
  const clean = String(name || '').trim();
  if (!clean) return 'AG';
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

// Normalizes a single method's parameter list, regardless of whether the
// source used `parameters` (Task Agent) or `params` (AI Agent tool catalog).
const normalizeParams = (rawParams, methodKey) => {
  const list = Array.isArray(rawParams) ? rawParams : [];
  return list.map((p, j) => {
    const isObj = typeof p === 'object' && p !== null;
    const paramName = isObj ? (p.name || '') : String(p);
    return {
      id: `p-${methodKey}-${j}-${paramName}`,
      name: paramName,
      type: isObj ? (p.type || 'text') : 'text',
      isOptional: isObj ? Boolean(p.isOptional) : false,
      default: isObj ? (p.default ?? null) : null,
      value: '',
    };
  });
};

// Task Agent shape: availableMethods (array of name strings) +
// methodDescriptions (object keyed by name, each { description, parameters }).
const normalizeTaskAgentMethods = (availableMethods, methodDescriptions) => {
  const methodNames = Array.isArray(availableMethods) ? availableMethods : [];
  const descriptions = methodDescriptions && typeof methodDescriptions === 'object' ? methodDescriptions : {};

  return methodNames.map((methodName, i) => {
    const info = descriptions[methodName] || {};
    return {
      id: `tool-${i}-${methodName}`,
      name: methodName,
      description: info.description || '',
      parameters: normalizeParams(info.parameters, `${i}-${methodName}`),
    };
  });
};

// AI Agent shape: a tool object (or array of tool objects) each carrying its
// own `methods` array, e.g. { handlerName, methods: [{ name, description, params }] }.
const normalizeAiAgentMethods = (handlerNames) => {
  const names = Array.isArray(handlerNames) ? handlerNames : [];
  const resolvedTools = names
    .map((handlerName) => toolNodes.find((t) => t.handlerName === handlerName))
    .filter(Boolean);

  return resolvedTools.flatMap((tool, ti) => {
    const methods = Array.isArray(tool?.methods) ? tool.methods : [];
    return methods.map((method, mi) => ({
      id: `tool-${ti}-${mi}-${method?.name || mi}`,
      name: method?.name || '',
      description: method?.description || '',
      parameters: normalizeParams(method?.params, `${ti}-${mi}`),
    }));
  });
};

// Reads the name of the single tool actually configured on the node —
// used only for the small badge in the agent list. Independent of the
// full method catalog used to populate the "Tools and Parameters" dropdown.
const getConfiguredToolName = (handlerNames) => {
  const names = Array.isArray(handlerNames) ? handlerNames : [];
  return names[0] || '';
};

const buildAgentsFromNodes = (nodes) => {
  const list = Array.isArray(nodes) ? nodes : [];
  return list.map((n, idx) => {
    const name = n?.data?.title || n?.data?.label || `Agent ${idx + 1}`;
    const kind = n?.type === 'task' ? 'Task Agent' : 'AI Agent';
    const availableMethods = kind === 'Task Agent'
      ? normalizeTaskAgentMethods(n?.data?.availableMethods, n?.data?.methodDescriptions)
      : normalizeAiAgentMethods(n?.data?.connectedConfig?.tool);
    return {
      id: n?.id || `agent-${idx}`,
      initials: getAgentInitials(name),
      color: KIND_COLORS[kind],
      name,
      kind,
      realToolName: getConfiguredToolName(n?.data?.connectedConfig?.tool),
      availableMethods,
      llm: n?.data?.llm || '',
      tools: [],
      expectedOutput: '',
      lastUpdated: '—',
    };
  });
};

const computeAgentStatus = (agent) => {
  const hasAnyTool = agent.tools.length > 0;
  const hasValidTool = agent.tools.some(
    (t) => t.name.trim() && t.parameters.some((p) => p.name.trim())
  );
  const hasExpectedOutput = Boolean(agent.expectedOutput && agent.expectedOutput.trim());

  if (!hasAnyTool && !hasExpectedOutput) return 'Not Configured';
  if (hasValidTool && hasExpectedOutput) return 'Configured';
  return 'Incomplete';
};

const buildExpectedFromAgents = (agentsList) =>
  (agentsList || [])
    .map((agent) => {
      const tools = (agent.tools || [])
        .filter((t) => t.name && t.name.trim())
        .map((t) => {
          const args = {};
          (t.parameters || []).forEach((p) => {
            if (p.name && p.name.trim()) {
              args[p.name] = p.value ?? '';
            }
          });
          return { name: t.name, arguments: args };
        });

      return {
        agent_id: agent.id,
        tools,
        expected_output: (agent.expectedOutput && String(agent.expectedOutput).trim()) || '',
      };
    })
    .filter((entry) => entry.tools.length > 0 || Boolean(entry.expected_output));

const toolsFromSavedExpected = (savedTools = []) =>
  (Array.isArray(savedTools) ? savedTools : []).map((t, i) => {
    const args = t?.arguments && typeof t.arguments === 'object' ? t.arguments : {};
    return {
      id: `tool-saved-${i}-${t?.name || i}`,
      name: t?.name || '',
      parameters: Object.entries(args).map(([name, value], j) => ({
        id: `p-saved-${i}-${j}`,
        name,
        value: value ?? '',
      })),
    };
  });

const mergeAgentsPreservingConfig = (nodes, prevAgents = [], savedExpected = []) => {
  const fresh = buildAgentsFromNodes(nodes);
  const prevById = new Map((prevAgents || []).map((a) => [a.id, a]));
  const savedById = new Map(
    (savedExpected || []).map((e) => [e.agent_id || e.agentId, e])
  );

  return fresh.map((agent) => {
    const prev = prevById.get(agent.id);
    const hasPrevConfig =
      prev &&
      ((Array.isArray(prev.tools) && prev.tools.length > 0) ||
        Boolean(prev.expectedOutput && String(prev.expectedOutput).trim()));

    if (hasPrevConfig) {
      return {
        ...agent,
        tools: prev.tools,
        expectedOutput: prev.expectedOutput || '',
      };
    }

    const saved = savedById.get(agent.id);
    if (saved) {
      return {
        ...agent,
        tools: toolsFromSavedExpected(saved.tools),
        expectedOutput: saved.expected_output || saved.expectedOutput || '',
      };
    }

    if (prev) {
      return {
        ...agent,
        tools: prev.tools || [],
        expectedOutput: prev.expectedOutput || '',
      };
    }

    return agent;
  });
};

const agentHasExpectedValue = (agent) => {
  const tools = agent?.tools || [];
  return tools.some((t) => {
    const hasName = Boolean(t?.name && String(t.name).trim());
    const hasParamValue = (t?.parameters || []).some(
      (p) =>
        Boolean(p?.name && String(p.name).trim()) ||
        (p?.value != null && String(p.value).trim() !== '')
    );
    return hasName || hasParamValue;
  });
};

const findAgentsMissingExpectedOutput = (agentsList) =>
  (agentsList || []).filter((agent) => {
    if (!agentHasExpectedValue(agent)) return false;
    return !(agent.expectedOutput && String(agent.expectedOutput).trim());
  });

const buildEvaluationExecuteFields = (
  isEvaluateEnabled,
  agentsList,
  { metricId = null } = {}
) => {
  const evaluate = Boolean(isEvaluateEnabled);
  const fields = { evaluate };

  if (evaluate) {
    fields.evaluationType = 'agent';
    const expected = buildExpectedFromAgents(agentsList);
    if (expected.length > 0) {
      fields.expected = expected;
    }
    if (metricId) {
      fields.metricId = metricId;
    }
  }

  return fields;
};

const getEvaluationFromProjectResponse = (res) => {
  const workflow =
    res?.workFlowTemplateDTO ||
    res?.projectDTO?.workFlowTemplateDTO ||
    res?.projectDTOS?.content?.[0]?.workFlowTemplateDTO ||
    null;
  const project = res?.projectDTO || res?.projectDTOS?.content?.[0] || null;

  // Prefer template fields; fall back to project-level if template has unset defaults.
  const templateEvaluate =
    typeof workflow?.evaluate === 'boolean' ? workflow.evaluate : undefined;
  const projectEvaluate =
    typeof project?.evaluate === 'boolean' ? project.evaluate : undefined;

  const evaluate =
    templateEvaluate === true || projectEvaluate === true
      ? true
      : templateEvaluate === false || projectEvaluate === false
        ? false
        : undefined;

  const expected = Array.isArray(workflow?.expected)
    ? workflow.expected
    : Array.isArray(project?.expected)
      ? project.expected
      : [];

  const metricId =
    workflow?.metricId ||
    workflow?.metricsId ||
    workflow?.metricCollectionId ||
    workflow?.metric?.id ||
    project?.metricId ||
    project?.metricsId ||
    project?.metricCollectionId ||
    null;

  return {
    evaluate,
    expected,
    metricId,
    workFlowTemplateId: workflow?.id || null,
  };
};

const DeleteConfirmPopover = ({ onConfirm, onCancel, label = 'Delete this?' }) => (
  <div
    className="sa-delete-confirm-popover"
    onMouseDown={(e) => e.stopPropagation()}
    style={{
      position: 'absolute',
      top: 'calc(100% + 6px)',
      right: 0,
      zIndex: 60,
      width: 200,
      padding: 10,
      borderRadius: 10,
      background: '#fff',
      border: '1px solid #e2e4e9',
      boxShadow: '0 12px 28px rgba(2,6,23,0.18)',
    }}
  >
    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
      {label}
    </div>
    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          height: 28, padding: '0 10px', borderRadius: 7,
          border: '1px solid #e2e4e9', background: '#fff', color: '#374151',
          fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        }}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        style={{
          height: 28, padding: '0 10px', borderRadius: 7, border: 'none',
          background: '#dc2626', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        }}
      >
        Delete
      </button>
    </div>
  </div>
);

const EvaluateModal = ({ isOpen, onClose, agentNodes = [], projectId = null, orchestratorId = null }) => {
  const [evaluateLoading, setEvaluateLoading] = useState(false);
  const [evaluateError, setEvaluateError] = useState(null);
  const [evaluateCollections, setEvaluateCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [isCollectionDropdownOpen, setIsCollectionDropdownOpen] = useState(false);
  const [collectionDropdownSearch, setCollectionDropdownSearch] = useState('');
  const [openMethodDropdownToolId, setOpenMethodDropdownToolId] = useState(null);
  const [methodDropdownSearch, setMethodDropdownSearch] = useState('');

  const [evaluateAgentsList, setEvaluateAgentsList] = useState(() => buildAgentsFromNodes(agentNodes));
  const [selectedEvaluateAgentId, setSelectedEvaluateAgentId] = useState(
    () => buildAgentsFromNodes(agentNodes)[0]?.id ?? null
  );
  const [evaluateAgentSearch, setEvaluateAgentSearch] = useState('');
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
  const [isEvaluateEnabled, setIsEvaluateEnabled] = useState(true);
  const [expectedOutputErrorIds, setExpectedOutputErrorIds] = useState(() => new Set());
  const [metricCollectionError, setMetricCollectionError] = useState(false);
  const [isEmptyConfigConfirmOpen, setIsEmptyConfigConfirmOpen] = useState(false);
  const hydratedProjectRef = React.useRef(null);
  const resolvedTemplateIdRef = React.useRef(orchestratorId || null);
  const lastSavedEvaluateRef = React.useRef(null);
  const expectedOutputRef = React.useRef(null);
  const metricCollectionRef = React.useRef(null);

  const getEvaluateDotColor = (id, idx) => EVALUATE_DOT_COLORS[idx % EVALUATE_DOT_COLORS.length];

  useEffect(() => {
    if (orchestratorId) {
      resolvedTemplateIdRef.current = orchestratorId;
    }
  }, [orchestratorId]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Keep typed agent config when reopening; only sync canvas agents in/out
  useEffect(() => {
    if (!isOpen) return;
    setEvaluateAgentsList((prev) => mergeAgentsPreservingConfig(agentNodes, prev, []));
    setSelectedEvaluateAgentId((prev) => {
      const fresh = buildAgentsFromNodes(agentNodes);
      if (prev && fresh.some((a) => a.id === prev)) return prev;
      return fresh[0]?.id ?? null;
    });
  }, [isOpen, agentNodes]);

  useEffect(() => {
    if (!isOpen) return;
    setIsEvaluateEnabled(true);
    lastSavedEvaluateRef.current = true;
    setMetricCollectionError(false);
  }, [isOpen]);

  const applyEvaluationToUi = useCallback((evaluation, nodes = agentNodes) => {
    if (!evaluation) return;

    if (evaluation.workFlowTemplateId) {
      resolvedTemplateIdRef.current = evaluation.workFlowTemplateId;
    }

    if (evaluation.metricId) {
      setSelectedCollectionId(evaluation.metricId);
    }

    if (evaluation.expected?.length > 0) {
      setEvaluateAgentsList((prev) =>
        mergeAgentsPreservingConfig(nodes, prev, evaluation.expected)
      );
    }
  }, [agentNodes]);

  useEffect(() => {
    if (!isOpen || !projectId) return;
    if (hydratedProjectRef.current === projectId) return;

    let cancelled = false;

    const hydrateFromProject = async () => {
      try {
        const res = await AgentService.getProject(0, { id: projectId });
        if (cancelled) return;

        const evaluation = getEvaluationFromProjectResponse(res);
        hydratedProjectRef.current = projectId;
        applyEvaluationToUi(evaluation, agentNodes);
      } catch (err) {
        console.warn('[EvaluateModal] failed to hydrate evaluation settings:', err);
      }
    };

    hydrateFromProject();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, agentNodes, applyEvaluationToUi]);

  const fetchEvaluationData = useCallback(async () => {
    setEvaluateLoading(true);
    setEvaluateError(null);
    try {
      const response = await EvaluationMetricsService.filterMetrics({ name: "" }, 0, 100);
      const mapped = (response?.metricDTOS?.content || []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        metrics: (item.metrics || []).filter((m) => m.enabled !== false),
      }));
      setEvaluateCollections(mapped);
    } catch (err) {
      setEvaluateError(
        err?.response?.data?.errorMessage || err?.message || 'Failed to load metric collections'
      );
    } finally {
      setEvaluateLoading(false);
    }
  }, []);

  // Fetch metric collections whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    fetchEvaluationData();
  }, [isOpen, fetchEvaluationData]);

  const runEvaluationViaSave = useCallback((evaluation) => {
    lastSavedEvaluateRef.current = Boolean(evaluation?.evaluate);
    setIsEvaluateEnabled(Boolean(evaluation?.evaluate));
    setExpectedOutputErrorIds(new Set());
    setMetricCollectionError(false);
    hydratedProjectRef.current = projectId;
    onClose?.();

    window.dispatchEvent(
      new CustomEvent('save-workflow', {
        detail: { evaluation },
      })
    );
    return true;
  }, [projectId, onClose]);

    const handleCreateEvaluation = useCallback(() => {
    if (!projectId) {
      showNotificationGlobal('Project is required to create evaluation.', 'error', 'bottomRight');
      return;
    }

    // Drop any empty tool rows (e.g. a row added via "+ Add Tool" where no
    // method was ever selected because none were available) before validating.
    const cleanedAgentsList = evaluateAgentsList.map((agent) => ({
      ...agent,
      tools: (agent.tools || []).filter((t) => t.name && t.name.trim()),
    }));
    if (cleanedAgentsList.some((a, i) => a.tools.length !== evaluateAgentsList[i].tools.length)) {
      setEvaluateAgentsList(cleanedAgentsList);
    }

    if (isEvaluateEnabled) {
      if (!selectedCollectionId) {
        setMetricCollectionError(true);
        showNotificationGlobal(
          'Please select a metric collection when Evaluate is enabled.',
          'error',
          'bottomRight'
        );
        setTimeout(() => {
          metricCollectionRef.current?.focus?.();
        }, 0);
        return;
      }

            const missingOutput = findAgentsMissingExpectedOutput(cleanedAgentsList);
      if (missingOutput.length > 0) {
        const names = missingOutput.map((a) => a.name || a.id).join(', ');
        showNotificationGlobal(
          `Expected output is required for: ${names}`,
          'error',
          'bottomRight'
        );
        setExpectedOutputErrorIds(new Set(missingOutput.map((a) => a.id)));
        setSelectedEvaluateAgentId(missingOutput[0].id);
        setTimeout(() => {
          expectedOutputRef.current?.focus?.();
        }, 0);
        return;
      }

      const expected = buildExpectedFromAgents(cleanedAgentsList);
      if (expected.length === 0) {
        setIsEmptyConfigConfirmOpen(true);
        return;
      }
    }

    const evaluation = buildEvaluationExecuteFields(isEvaluateEnabled, cleanedAgentsList, {
      metricId: selectedCollectionId,
    });
    runEvaluationViaSave(evaluation);
  }, [
    projectId,
    isEvaluateEnabled,
    evaluateAgentsList,
    selectedCollectionId,
    runEvaluationViaSave,
  ]);

  const handleSaveEvaluateOnly = useCallback(() => {
    if (!selectedCollectionId) {
      setIsEmptyConfigConfirmOpen(false);
      setMetricCollectionError(true);
      showNotificationGlobal(
        'Please select a metric collection when Evaluate is enabled.',
        'error',
        'bottomRight'
      );
      setTimeout(() => {
        metricCollectionRef.current?.focus?.();
      }, 0);
      return;
    }

    setIsEmptyConfigConfirmOpen(false);
    const evaluation = buildEvaluationExecuteFields(true, [], {
      metricId: selectedCollectionId,
    });
    runEvaluationViaSave(evaluation);
  }, [selectedCollectionId, runEvaluationViaSave]);

  const handleStayToAddConfiguration = useCallback(() => {
    setIsEmptyConfigConfirmOpen(false);
    const firstAgent = evaluateAgentsList[0];
    if (firstAgent) {
      setSelectedEvaluateAgentId(firstAgent.id);
    }
  }, [evaluateAgentsList]);

  const updateSelectedEvaluateAgentField = useCallback((field, value) => {
    setEvaluateAgentsList((prev) =>
      prev.map((a) => (a.id === selectedEvaluateAgentId ? { ...a, [field]: value } : a))
    );
    if (field === 'expectedOutput' && value && String(value).trim()) {
      setExpectedOutputErrorIds((prev) => {
        if (!prev.has(selectedEvaluateAgentId)) return prev;
        const next = new Set(prev);
        next.delete(selectedEvaluateAgentId);
        return next;
      });
    }
  }, [selectedEvaluateAgentId]);

    const handleAddTool = useCallback(() => {
    setEvaluateAgentsList((prev) =>
      prev.map((a) => {
        if (a.id !== selectedEvaluateAgentId) return a;
        const newTool = {
          id: `tool-${Date.now()}`,
          name: '',
          parameters: [],
        };
        return { ...a, tools: [...a.tools, newTool] };
      })
    );
  }, [selectedEvaluateAgentId]);

  const handleSelectToolMethod = useCallback((toolId, methodName) => {
    setEvaluateAgentsList((prev) =>
      prev.map((a) => {
        if (a.id !== selectedEvaluateAgentId) return a;
        const method = (a.availableMethods || []).find((m) => m.name === methodName);
        return {
          ...a,
          tools: a.tools.map((t) => {
            if (t.id !== toolId) return t;
            const prevValueByName = new Map((t.parameters || []).map((p) => [p.name, p.value]));
            return {
              ...t,
              name: methodName,
              parameters: (method?.parameters || []).map((p, j) => ({
                id: `p-${toolId}-${p.name || j}`,
                name: p.name,
                value: prevValueByName.get(p.name) ?? '',
              })),
            };
          }),
        };
      })
    );
  }, [selectedEvaluateAgentId]);

  const handleRemoveTool = useCallback((toolId) => {
    setEvaluateAgentsList((prev) =>
      prev.map((a) =>
        a.id === selectedEvaluateAgentId
          ? { ...a, tools: a.tools.filter((t) => t.id !== toolId) }
          : a
      )
    );
  }, [selectedEvaluateAgentId]);

  const handleUpdateParameter = useCallback((toolId, paramId, field, value) => {
    setEvaluateAgentsList((prev) =>
      prev.map((a) =>
        a.id === selectedEvaluateAgentId
          ? {
              ...a,
              tools: a.tools.map((t) =>
                t.id === toolId
                  ? {
                      ...t,
                      parameters: t.parameters.map((p) =>
                        p.id === paramId ? { ...p, [field]: value } : p
                      ),
                    }
                  : t
              ),
            }
          : a
      )
    );
  }, [selectedEvaluateAgentId]);

  

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Evaluate — metric collections and agents"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 18,
          width: 1120,
          maxWidth: '96vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 28px 70px rgba(2,6,23,0.32)',
          overflow: 'hidden',
          animation: 'sa-pop-in 180ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 26px',
            borderBottom: '1px solid #eef0f3',
            flexShrink: 0,
            background: 'linear-gradient(180deg, rgba(99,102,241,0.035) 0%, rgba(99,102,241,0) 100%)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 42, height: 42, borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                boxShadow: '0 6px 16px rgba(99,102,241,0.30)', flexShrink: 0,
              }}
            >
              <i className="fa-solid fa-clipboard-check" style={{ fontSize: 17, color: '#ffffff' }} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                Evaluate
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 3 }}>
                Choose a metric collection and configure agents
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <label
              htmlFor="evaluate-enabled-switch"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                margin: 0,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                Evaluate
              </span>
              <span
                style={{
                  position: 'relative',
                  width: 40,
                  height: 22,
                  flexShrink: 0,
                }}
              >
                <input
                  id="evaluate-enabled-switch"
                  type="checkbox"
                  role="switch"
                  checked={isEvaluateEnabled}
                  onChange={(e) => {
                    const next = e.target.checked;
                    lastSavedEvaluateRef.current = next;
                    setIsEvaluateEnabled(next);
                    if (!next) setMetricCollectionError(false);
                  }}
                  aria-checked={isEvaluateEnabled}
                  aria-label="Enable evaluate"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 1,
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    width: 40,
                    height: 22,
                    borderRadius: 999,
                    background: isEvaluateEnabled ? '#6366f1' : '#d1d5db',
                    transition: 'background 160ms ease',
                    boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.06)',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: isEvaluateEnabled ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 3px rgba(15,23,42,0.25)',
                      transition: 'left 160ms ease',
                    }}
                  />
                </span>
              </span>
           
            </label>

            <button
              type="button"
              onClick={() => onClose?.()}
              aria-label="Close evaluate modal"
              style={{
                width: 30, height: 30, borderRadius: 8, border: 'none',
                background: 'transparent', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#9ca3af',
                flexShrink: 0, transition: 'background 120ms ease, color 120ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: 15 }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 22, overflowY: 'auto', flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Top row: Metric collection + Metrics preview */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div
              style={{
                flex: '0 0 340px', position: 'relative', border: '1px solid #eef0f3',
                borderRadius: 14, padding: 16, background: '#fbfcfe',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span
                  style={{
                    width: 22, height: 22, borderRadius: 6, background: 'rgba(99,102,241,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-database" style={{ fontSize: 10, color: '#6366f1' }} />
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: metricCollectionError ? '#dc2626' : '#1e293b',
                  }}
                >
                  Metric collection
                  {metricCollectionError ? (
                    <span style={{ fontWeight: 700, fontSize: 12, marginLeft: 6 }}>
                      (required)
                    </span>
                  ) : null}
                </span>
              </div>

              {(() => {
                const selected = evaluateCollections.find((c) => c.id === selectedCollectionId) || null;
                const selectedIdx = selected ? evaluateCollections.findIndex((c) => c.id === selected.id) : -1;

                return (
                  <div style={{ position: 'relative' }}>
                    <button
                      ref={metricCollectionRef}
                      type="button"
                      onClick={() => setIsCollectionDropdownOpen((v) => !v)}
                      aria-invalid={metricCollectionError}
                      style={{
                        width: '100%', height: 44, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', padding: '0 14px', borderRadius: 10,
                        border: metricCollectionError
                          ? '1.5px solid #ef4444'
                          : isCollectionDropdownOpen
                            ? '1.5px solid #6366f1'
                            : '1.5px solid #e2e4e9',
                        background: metricCollectionError ? '#fef2f2' : '#fff',
                        cursor: 'pointer',
                        boxShadow: metricCollectionError
                          ? '0 0 0 3px rgba(239,68,68,0.15)'
                          : isCollectionDropdownOpen
                            ? '0 0 0 3px rgba(99,102,241,0.12)'
                            : 'none',
                        transition: 'border-color 120ms ease, box-shadow 120ms ease',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        {selected ? (
                          <span
                            style={{
                              width: 8, height: 8, borderRadius: '50%',
                              background: getEvaluateDotColor(selected.id, selectedIdx), flexShrink: 0,
                            }}
                          />
                        ) : (
                          <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }} />
                        )}
                        <span
                          style={{
                            fontSize: 13.5, fontWeight: 700, color: selected ? '#0f172a' : '#9ca3af',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {selected ? selected.name : 'Select a collection...'}
                        </span>
                      </span>
                      <i
                        className="fa-solid fa-chevron-down"
                        style={{
                          fontSize: 11, color: '#9ca3af', transition: 'transform 180ms ease',
                          transform: isCollectionDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0,
                        }}
                      />
                    </button>

                    {isCollectionDropdownOpen ? (
                      <div
                        style={{
                          position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 40,
                          background: '#fff', borderRadius: 12, border: '1px solid #e2e4e9',
                          boxShadow: '0 20px 48px rgba(2,6,23,0.22)', maxHeight: 280,
                          display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}
                      >
                        <div style={{ padding: 10, borderBottom: '1px solid #f1f2f4', flexShrink: 0 }}>
                          <div style={{ position: 'relative' }}>
                            <i
                              className="fa-solid fa-magnifying-glass"
                              style={{
                                position: 'absolute', left: 10, top: '50%',
                                transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af',
                              }}
                            />
                            <input
                              type="text"
                              autoFocus
                              placeholder="Search collections..."
                              value={collectionDropdownSearch}
                              onChange={(e) => setCollectionDropdownSearch(e.target.value)}
                              style={{
                                width: '100%', height: 32, borderRadius: 7, border: '1px solid #e2e4e9',
                                padding: '0 10px 0 28px', fontSize: 12.5, outline: 'none',
                              }}
                            />
                          </div>
                        </div>

                        <div style={{ overflowY: 'auto', padding: 6 }}>
                          {evaluateLoading ? (
                            <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: 12.5 }}>
                              <i className="fa-solid fa-spinner" style={{ marginRight: 6, animation: 'sa-spin 1s linear infinite' }} />
                              Loading...
                            </div>
                          ) : evaluateError ? (
                            <div style={{ textAlign: 'center', padding: '20px 10px', color: '#b91c1c', fontSize: 12 }}>
                              {evaluateError}
                            </div>
                          ) : (
                            (() => {
                              const filtered = evaluateCollections.filter((c) =>
                                c.name.toLowerCase().includes(collectionDropdownSearch.toLowerCase())
                              );
                              if (filtered.length === 0) {
                                return (
                                  <div style={{ textAlign: 'center', padding: '24px 10px', color: '#9ca3af', fontSize: 12.5 }}>
                                    {evaluateCollections.length === 0
                                      ? 'No metric collections yet.'
                                      : `No matches for "${collectionDropdownSearch}"`}
                                  </div>
                                );
                              }
                              return filtered.map((c) => {
                                const origIdx = evaluateCollections.findIndex((x) => x.id === c.id);
                                const isSel = c.id === selectedCollectionId;
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedCollectionId(c.id);
                                      setMetricCollectionError(false);
                                      setIsCollectionDropdownOpen(false);
                                      setCollectionDropdownSearch('');
                                    }}
                                    style={{
                                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                      padding: '9px 10px', borderRadius: 8, border: 'none',
                                      background: isSel ? 'rgba(99,102,241,0.08)' : 'transparent',
                                      cursor: 'pointer', textAlign: 'left', transition: 'background 120ms ease',
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSel) e.currentTarget.style.background = '#f8f9fb';
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSel) e.currentTarget.style.background = 'transparent';
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: getEvaluateDotColor(c.id, origIdx), flexShrink: 0,
                                      }}
                                    />
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: 13, fontWeight: 700, color: '#0f172a',
                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {c.name}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                                        {c.metrics.length} metric{c.metrics.length === 1 ? '' : 's'}
                                      </div>
                                    </span>
                                    {isSel ? (
                                      <i className="fa-solid fa-check" style={{ fontSize: 12, color: '#6366f1', flexShrink: 0 }} />
                                    ) : null}
                                  </button>
                                );
                              });
                            })()
                          )}
                        </div>
                      </div>
                    ) : null}
                    {metricCollectionError ? (
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#dc2626' }}>
                        Metric collection is required when Evaluate is enabled.
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>

            <div
              style={{
                flex: 1, border: '1px solid #eef0f3', borderRadius: 14,
                padding: 16, background: '#fbfcfe', minWidth: 0,
              }}
            >
              {(() => {
                const selected = evaluateCollections.find((c) => c.id === selectedCollectionId) || null;
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>
                        Metrics in this collection
                      </span>
                      {selected ? (
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af' }}>
                          ({selected.metrics.length})
                        </span>
                      ) : null}
                    </div>
                    {!selected ? (
                      <div style={{ fontSize: 12.5, color: '#9ca3af', fontWeight: 600 }}>
                        Select a metric collection to see its metrics.
                      </div>
                    ) : selected.metrics.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: '#9ca3af', fontWeight: 600 }}>
                        No metrics enabled in this collection.
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                          gap: 8,
                        }}
                      >
                        {selected.metrics.map((m, j) => {
                          const color = METRIC_PILL_COLORS[j % METRIC_PILL_COLORS.length];
                          return (
                            <span
                              key={m.metricName}
                              title={m.metricName}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, height: 30,
                                padding: '0 10px', borderRadius: 999, background: '#fff',
                                border: `1px solid ${color}40`, color: '#1e293b', fontSize: 12.5,
                                fontWeight: 700, boxShadow: '0 1px 2px rgba(2,6,23,0.05)',
                                minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden',
                                textOverflow: 'ellipsis', maxWidth: '100%',
                              }}
                            >
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {m.metricName}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Bottom row: Agents list + Agent detail */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', minHeight: 460 }}>
            <div
              style={{
                flex: '0 0 340px', border: '1px solid #eef0f3', borderRadius: 14, padding: 16,
                background: '#fbfcfe', display: 'flex', flexDirection: 'column', minHeight: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 22, height: 22, borderRadius: 6, background: 'rgba(99,102,241,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <i className="fa-solid fa-user-group" style={{ fontSize: 10, color: '#6366f1' }} />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Agents</span>
                </div>
                {(() => {
                  const configuredCount = evaluateAgentsList.filter((a) => computeAgentStatus(a) === 'Configured').length;
                  const total = evaluateAgentsList.length;
                  return (
                    <span
                      title={`${configuredCount} of ${total} agents configured`}
                      style={{
                        fontSize: 11, fontWeight: 800,
                        color: configuredCount === total ? '#15803d' : '#64748b',
                        background: configuredCount === total ? 'rgba(34,197,94,0.10)' : 'rgba(15,23,42,0.05)',
                        padding: '3px 9px', borderRadius: 999, flexShrink: 0,
                      }}
                    >
                      {configuredCount}/{total} configured
                    </span>
                  );
                })()}
              </div>

              <div style={{ position: 'relative', marginBottom: 10 }}>
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}
                />
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={evaluateAgentSearch}
                  onChange={(e) => setEvaluateAgentSearch(e.target.value)}
                  style={{
                    width: '100%', height: 34, borderRadius: 8, border: '1px solid #e2e4e9',
                    padding: '0 10px 0 28px', fontSize: 12.5, outline: 'none', background: '#fff',
                  }}
                />
              </div>

              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
                {evaluateAgentsList
                  .filter((a) => a.name.toLowerCase().includes(evaluateAgentSearch.toLowerCase()))
                  .map((a) => {
                    const isSel = a.id === selectedEvaluateAgentId;
                    const agentStatus = computeAgentStatus(a);
                    const statusStyle = AGENT_STATUS_STYLES[agentStatus] || AGENT_STATUS_STYLES.Configured;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setSelectedEvaluateAgentId(a.id)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 10px', borderRadius: 10,
                          border: isSel ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                          background: isSel ? 'rgba(99,102,241,0.07)' : 'transparent',
                          cursor: 'pointer', textAlign: 'left',
                          transition: 'background 120ms ease, border-color 120ms ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSel) e.currentTarget.style.background = '#f1f2f6';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSel) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <span
                          style={{
                            width: 30, height: 30, borderRadius: '50%', background: `${a.color}1a`,
                            color: a.color, fontSize: 11, fontWeight: 900, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          {a.kind === 'Task Agent' ? <TaskEngineIcon size="sm" /> : <FaRobot size={14} />}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13, fontWeight: 700, color: '#0f172a',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            {a.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusStyle.dot }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: statusStyle.text }}>{agentStatus}</span>
                          </div>
                          {(a.realToolName || a.llm) ? (
                            <div
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5, marginTop: 3,
                                fontSize: 11, fontWeight: 600, color: '#64748b',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}
                            >
                              {a.realToolName ? (
                                <>
                                  <FaCogs size={12} style={{ color: '#6366f1', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {a.realToolName}
                                  </span>
                                </>
                              ) : null}
                              {a.realToolName && a.llm ? <span style={{ flexShrink: 0, opacity: 0.5 }}>|</span> : null}
                              {a.llm ? (
                                <>
                                  <FaBrain size={12} style={{ color: '#6366f1', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.llm}</span>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                        </span>
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: 11, color: '#c3c8d1', flexShrink: 0 }} />
                      </button>
                    );
                  })}
              </div>
            </div>

            <div
              style={{
                flex: 1, border: '1px solid #eef0f3', borderRadius: 14, padding: 18,
                background: '#fbfcfe', minWidth: 0, display: 'flex', flexDirection: 'column',
              }}
            >
              {(() => {
                const agent = evaluateAgentsList.find((a) => a.id === selectedEvaluateAgentId) || null;
                if (!agent) {
                  return (
                    <div style={{ margin: 'auto', color: '#9ca3af', fontSize: 13, fontWeight: 600 }}>
                      Select an agent to configure it.
                    </div>
                  );
                }
                const agentStatus = computeAgentStatus(agent);
                const statusStyle = AGENT_STATUS_STYLES[agentStatus] || AGENT_STATUS_STYLES.Configured;
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                      <span
                        style={{
                          width: 34, height: 34, borderRadius: '50%', background: `${agent.color}1a`,
                          color: agent.color, fontSize: 12, fontWeight: 900, display: 'flex',
                          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                      >
                        {agent.kind === 'Task Agent' ? <TaskEngineIcon size="sm" /> : <FaRobot size={16} />}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{agent.name}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusStyle.dot }} />
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: statusStyle.text }}>{agentStatus}</span>
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                      {/* Tools and Parameters */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Tools and Parameters</span>
                          <button
                            type="button"
                            onClick={handleAddTool}
                            style={{
                              height: 28, padding: '0 10px', borderRadius: 8, border: 'none',
                              background: 'transparent', color: '#4f46e5', fontSize: 12.5, fontWeight: 800,
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <i className="fa-solid fa-plus" style={{ fontSize: 10 }} />
                            Add Tool
                          </button>
                        </div>

                        {agent.tools.length === 0 ? (
                          <div
                            style={{
                              padding: 16, borderRadius: 10, border: '1px dashed #e2e4e9',
                              textAlign: 'center', color: '#9ca3af', fontSize: 12.5, fontWeight: 600,
                            }}
                          >
                            No tools added yet.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {agent.tools.map((tool) => (
                              <div
                                key={tool.id}
                                style={{ borderRadius: 12, border: '1px solid #eef0f3', background: '#fff', padding: 14 }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                  <i className="fa-solid fa-grip-vertical" style={{ fontSize: 12, color: '#c3c8d1' }} />
                                                                    <div style={{ flex: 1, position: 'relative' }}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenMethodDropdownToolId((prev) => {
                                          const next = prev === tool.id ? null : tool.id;
                                          setMethodDropdownSearch('');
                                          return next;
                                        })
                                      }
                                      style={{
                                        width: '100%', height: 36, display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between', padding: '0 10px', borderRadius: 8,
                                        border: openMethodDropdownToolId === tool.id
                                          ? '1.5px solid #6366f1'
                                          : '1px solid #e2e4e9',
                                        background: '#fff', cursor: 'pointer',
                                        boxShadow: openMethodDropdownToolId === tool.id
                                          ? '0 0 0 3px rgba(99,102,241,0.12)'
                                          : 'none',
                                      }}
                                    >
                                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                        {tool.name ? (
                                          <span style={{
                                            width: 7, height: 7, borderRadius: '50%',
                                            background: EVALUATE_DOT_COLORS[
                                              (agent.availableMethods || []).findIndex((m) => m.name === tool.name) % EVALUATE_DOT_COLORS.length
                                            ] || '#6366f1', flexShrink: 0,
                                          }} />
                                        ) : null}
                                        <span style={{
                                          fontSize: 12.5, fontWeight: tool.name ? 700 : 500,
                                          color: tool.name ? '#0f172a' : '#9ca3af',
                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                          {tool.name || ((agent.availableMethods || []).length === 0
                                            ? 'No methods available'
                                            : 'Select a method...')}
                                        </span>
                                      </span>
                                      <i
                                        className="fa-solid fa-chevron-down"
                                        style={{
                                          fontSize: 10, color: '#9ca3af', flexShrink: 0,
                                          transition: 'transform 180ms ease',
                                          transform: openMethodDropdownToolId === tool.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                      />
                                    </button>

                                    {openMethodDropdownToolId === tool.id ? (
                                      <div
                                        style={{
                                          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
                                          background: '#fff', borderRadius: 10, border: '1px solid #e2e4e9',
                                          boxShadow: '0 16px 40px rgba(2,6,23,0.18)', maxHeight: 260,
                                          display: 'flex', flexDirection: 'column', overflow: 'hidden',
                                        }}
                                      >
                                        <div style={{ padding: 8, borderBottom: '1px solid #f1f2f4', flexShrink: 0 }}>
                                          <input
                                            type="text"
                                            autoFocus
                                            placeholder="Search methods..."
                                            value={methodDropdownSearch}
                                            onChange={(e) => setMethodDropdownSearch(e.target.value)}
                                            style={{
                                              width: '100%', height: 30, borderRadius: 7, border: '1px solid #e2e4e9',
                                              padding: '0 10px', fontSize: 12, outline: 'none',
                                            }}
                                          />
                                        </div>
                                        <div style={{ overflowY: 'auto', padding: 6 }}>
                                          {(() => {
                                            const methods = agent.availableMethods || [];
                                            const usedElsewhere = new Set(
                                              (agent.tools || [])
                                                .filter((t) => t.id !== tool.id && t.name)
                                                .map((t) => t.name)
                                            );
                                            const filtered = methods.filter((m) =>
                                              !usedElsewhere.has(m.name) &&
                                              m.name.toLowerCase().includes(methodDropdownSearch.toLowerCase())
                                            );
                                            if (filtered.length === 0) {
                                              return (
                                                <div style={{ textAlign: 'center', padding: '18px 10px', color: '#9ca3af', fontSize: 12 }}>
                                                  {methods.length === 0 ? 'No methods available.' : 'No matches.'}
                                                </div>
                                              );
                                            }
                                            return filtered.map((m) => {
                                              const origIdx = methods.findIndex((x) => x.name === m.name);
                                              const isSel = m.name === tool.name;
                                              return (
                                                <button
                                                  key={m.id || m.name}
                                                  type="button"
                                                  onClick={() => {
                                                    handleSelectToolMethod(tool.id, m.name);
                                                    setOpenMethodDropdownToolId(null);
                                                    setMethodDropdownSearch('');
                                                  }}
                                                  style={{
                                                    width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                                                    padding: '8px 9px', borderRadius: 7, border: 'none',
                                                    background: isSel ? 'rgba(99,102,241,0.08)' : 'transparent',
                                                    cursor: 'pointer', textAlign: 'left',
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!isSel) e.currentTarget.style.background = '#f8f9fb';
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSel) e.currentTarget.style.background = 'transparent';
                                                  }}
                                                >
                                                  <span style={{
                                                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                                                    background: EVALUATE_DOT_COLORS[origIdx % EVALUATE_DOT_COLORS.length],
                                                  }} />
                                                  <span style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                      fontSize: 12.5, fontWeight: 700, color: '#0f172a',
                                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}>
                                                      {m.name}
                                                    </div>
                                                    {m.description ? (
                                                      <div style={{
                                                        fontSize: 10.5, color: '#9ca3af', marginTop: 1,
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                      }}>
                                                        {m.description}
                                                      </div>
                                                    ) : null}
                                                  </span>
                                                  {isSel ? (
                                                    <i className="fa-solid fa-check" style={{ fontSize: 11, color: '#6366f1', flexShrink: 0 }} />
                                                  ) : null}
                                                </button>
                                              );
                                            });
                                          })()}
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                  <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setDeleteConfirmTarget((prev) =>
                                          prev?.type === 'tool' && prev?.toolId === tool.id
                                            ? null
                                            : { type: 'tool', toolId: tool.id }
                                        )
                                      }
                                      aria-label="Remove tool"
                                      style={{
                                        width: 28, height: 28, borderRadius: 7, border: 'none',
                                        background: 'rgba(239,68,68,0.08)', color: '#dc2626', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}
                                    >
                                      <i className="fa-regular fa-trash-can" style={{ fontSize: 12 }} />
                                    </button>
                                    {deleteConfirmTarget?.type === 'tool' && deleteConfirmTarget?.toolId === tool.id ? (
                                      <DeleteConfirmPopover
                                        label="Delete this tool and all its parameters?"
                                        onCancel={() => setDeleteConfirmTarget(null)}
                                        onConfirm={() => {
                                          handleRemoveTool(tool.id);
                                          setDeleteConfirmTarget(null);
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                </div>

                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {tool.parameters.map((param) => (
                                    <div key={param.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      <div
                                        style={{
                                          flex: 1, height: 36, borderRadius: 8, border: '1px solid #eef0f3',
                                          padding: '0 10px', fontSize: 12.5, display: 'flex', alignItems: 'center',
                                          gap: 6, background: '#f8f9fb', color: '#475569', fontWeight: 600,
                                        }}
                                      >
                                        <span>{param.name}</span>
                                        {param.isOptional ? (
                                          <span style={{ fontSize: 10.5, color: '#9ca3af', fontStyle: 'italic', fontWeight: 500 }}>
                                            (optional)
                                          </span>
                                        ) : null}
                                      </div>
                                      <input
                                        value={param.value}
                                        onChange={(e) => handleUpdateParameter(tool.id, param.id, 'value', e.target.value)}
                                        placeholder="Value (text)"
                                        style={{
                                          flex: 1, height: 36, borderRadius: 8, border: '1px solid #e2e4e9',
                                          padding: '0 10px', fontSize: 12.5, outline: 'none', background: '#fff',
                                        }}
                                      />
                                    </div>
                                  ))}
                                  {tool.name && tool.parameters.length === 0 ? (
                                    <div style={{ fontSize: 11.5, color: '#9ca3af', fontStyle: 'italic' }}>
                                      This method has no parameters.
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Expected Output */}
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: expectedOutputErrorIds.has(agent.id) ? '#dc2626' : '#1e293b',
                            marginBottom: 8,
                          }}
                        >
                          Expected Output
                          {expectedOutputErrorIds.has(agent.id) ? (
                            <span style={{ fontWeight: 700, fontSize: 12, marginLeft: 6 }}>
                              (required)
                            </span>
                          ) : null}
                        </div>
                        <div style={{ position: 'relative' }}>
                          <textarea
                            ref={expectedOutputRef}
                            value={agent.expectedOutput}
                            onChange={(e) => updateSelectedEvaluateAgentField('expectedOutput', e.target.value.slice(0, 2000))}
                            rows={4}
                            placeholder="Describe the expected output from the agent when the tools are used..."
                            aria-invalid={expectedOutputErrorIds.has(agent.id)}
                            style={{
                              width: '100%',
                              borderRadius: 10,
                              border: expectedOutputErrorIds.has(agent.id)
                                ? '1.5px solid #ef4444'
                                : '1px solid #e2e4e9',
                              padding: '10px 12px',
                              fontSize: 13,
                              outline: 'none',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: 1.5,
                              background: expectedOutputErrorIds.has(agent.id) ? '#fef2f2' : '#fff',
                              boxShadow: expectedOutputErrorIds.has(agent.id)
                                ? '0 0 0 3px rgba(239,68,68,0.15)'
                                : 'none',
                            }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              right: 12,
                              bottom: 8,
                              fontSize: 11,
                              color: '#9ca3af',
                              background: expectedOutputErrorIds.has(agent.id) ? '#fef2f2' : '#fff',
                            }}
                          >
                            {agent.expectedOutput.length}/2000
                          </span>
                        </div>
                        {expectedOutputErrorIds.has(agent.id) ? (
                          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#dc2626' }}>
                            Expected output cannot be empty when tools are configured for this agent.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px',
            borderTop: '1px solid #eef0f3', flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => onClose?.()}
            style={{
              height: 40, padding: '0 20px', borderRadius: 10, border: '1px solid #e2e4e9',
              background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleCreateEvaluation}
            disabled={!projectId}
            style={{
              height: 40, padding: '0 22px', borderRadius: 10, border: 'none',
              background: !projectId
                ? '#c7d2fe'
                : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: '#fff',
              fontSize: 13, fontWeight: 700,
              cursor: !projectId ? 'not-allowed' : 'pointer',
              boxShadow: !projectId ? 'none' : '0 4px 14px rgba(99,102,241,0.35)',
              display: 'flex', alignItems: 'center', gap: 8,
              opacity: !projectId ? 0.75 : 1,
            }}
          >
            <i className="fa-solid fa-play" style={{ fontSize: 11 }} />
            Create Evaluation
          </button>
        </div>
      </div>

      <SAModalConfirm
        show={isEmptyConfigConfirmOpen}
        zIndex={2200}
        title="No configuration added"
        message="No configuration is added. Do you want to add?"
        cancelText="Yes"
        okText="No"
        handleCancel={handleStayToAddConfiguration}
        handleOk={handleSaveEvaluateOnly}
      />
    </div>
  );
};

export default React.memo(EvaluateModal);